import type {
  HealthResponse,
  MeResponse,
  PropertyDetail,
  PropertyListResponse,
} from "@screener/contracts";
import type { HardFilterResult } from "@screener/domain";

/** 같은 출처의 /api 를 호출한다. 로컬에서는 Vite 프록시가 Worker 로 넘긴다. */
const BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export interface PropertyDetailResponse {
  property: PropertyDetail;
  hardFilter: HardFilterResult;
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * 세션 토큰을 읽어오는 함수. SessionProvider 가 연결한다.
 *
 * 토큰을 모듈 변수에 복사해 두지 않고 매 요청에서 읽는다.
 * 갱신된 토큰을 놓치면 로그인이 멀쩡한데 401 이 나는 상태가 된다.
 */
let tokenProvider: () => string | null = () => null;

export function setAccessTokenProvider(fn: () => string | null) {
  tokenProvider = fn;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("accept", "application/json");
  const token = tokenProvider();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  const type = res.headers.get("content-type") ?? "";
  if (!type.includes("application/json")) {
    // /api 가 HTML 을 돌려주면 라우팅이 깨진 것이다. 조용히 넘기지 않는다.
    throw new ApiFailure(res.status, "non_json_response", `JSON 이 아닌 응답: ${type}`);
  }
  const body = (await res.json()) as unknown;
  if (!res.ok) {
    const err = body as { error?: { code?: string; message?: string } };
    throw new ApiFailure(
      res.status,
      err.error?.code ?? "unknown",
      err.error?.message ?? "요청이 실패했다",
    );
  }
  return body as T;
}

export const api = {
  health: () => request<HealthResponse>("/api/v1/health"),
  me: () => request<MeResponse>("/api/v1/me"),
  properties: (search: string) =>
    request<PropertyListResponse>(`/api/v1/properties${search ? `?${search}` : ""}`),
  property: (id: string) =>
    request<PropertyDetailResponse>(`/api/v1/properties/${encodeURIComponent(id)}`),
};
